import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCampaignContentBlocks, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignContentBlock } from '../../../../support/campaigns';
import { SectionHeader, builderStyles } from './primitives';
import { TokenTextField } from './TokenTextField';
import {
  PrimitiveField,
  STEP_LAYOUT_PRIMITIVES,
  primitiveDef,
  primitiveLabel,
} from './stepLayoutCatalog';

/**
 * The step-layout composer — the drag-drop authoring surface for a runner step's primitive *body*.
 *
 * A runner step's body is a `CampaignContentBlock` in the `step_layout` category whose payload holds an
 * ordered `primitives: Array<{ key, config? }>`; `resolveStepLayout` reads it and `StepBodyRenderer`
 * renders each primitive in order (DB is the sole source of truth — no bundled whole-body fallback). The
 * block is matched to a step by its runner kind (`payload.stepKind`), which is the `metadata.runnerKind`
 * the step editor stores. There is one body layout per runner kind, so this composer is scoped by
 * `runnerKind` and edits (or lazily creates) that single block.
 *
 * Authoring: drag a primitive from the palette onto the body (or click its ＋), reorder body items by
 * dragging, edit each primitive's optional config inline, and remove one with the trash button. Every
 * change persists the whole block via the builder's full-record PUT, mirroring the other editors.
 */

interface Entry {
  /** A stable per-instance id used only client-side (never persisted). */
  id: string;
  /** The registered primitive key. */
  key: string;
  config: Record<string, unknown>;
}

const STEP_LAYOUT_CATEGORY = 'step_layout';

let seq = 0;
const freshId = (): string => `pl_${Date.now().toString(36)}_${(seq++).toString(36)}`;
const isInstanceId = (id: unknown): id is string => typeof id === 'string' && id.startsWith('pl_');

/** Read the persisted `primitives` array into local entries, tolerating bare-string + legacy shapes. */
function readEntries(block?: CampaignContentBlock | null): Entry[] {
  const prims = (block?.payload as { primitives?: unknown } | undefined)?.primitives;
  if (!Array.isArray(prims)) return [];
  return prims
    .map((p): Entry => {
      if (typeof p === 'string') return { id: freshId(), key: p, config: {} };
      const raw = p as { key?: unknown; primitive?: unknown; config?: unknown };
      const key =
        typeof raw.key === 'string' ? raw.key : typeof raw.primitive === 'string' ? raw.primitive : '';
      const config =
        raw.config && typeof raw.config === 'object' ? { ...(raw.config as Record<string, unknown>) } : {};
      return { id: freshId(), key, config };
    })
    .filter((e) => e.key);
}

/** Drop blank/empty config values so the persisted payload only carries meaningful overrides. */
function cleanConfig(config: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(config).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    out[k] = typeof v === 'string' ? v.trim() : v;
  });
  return out;
}

/** The persisted `primitives` array for a set of entries (config omitted entirely when empty). */
function buildPrimitives(entries: Entry[]): Array<{ key: string; config?: Record<string, unknown> }> {
  return entries.map((e) => {
    const config = cleanConfig(e.config);
    return Object.keys(config).length ? { key: e.key, config } : { key: e.key };
  });
}

/** Order+key+config signature, to skip redundant persists (e.g. ReactSortable's echo on mount). */
const entriesShape = (entries: Entry[]): string =>
  JSON.stringify(entries.map((e) => [e.key, cleanConfig(e.config)]));

export function StepLayoutComposer(props: {
  campaign: Campaign;
  canWrite: boolean;
  /** The step's runner kind (`metadata.runnerKind`) — the discriminator this body layout is keyed by. */
  runnerKind: string;
}): JSX.Element {
  const { campaign, canWrite, runnerKind } = props;

  const { data: rows = [], isLoading } = useCampaignContentBlocks(campaign.id, STEP_LAYOUT_CATEGORY);
  const mut = useCampaignMutations('contentBlock');

  // The single body block for this runner kind: prefer an explicit payload.stepKind match, else a
  // key match (the runner accepts either), else none yet.
  const block = React.useMemo<CampaignContentBlock | null>(() => {
    const byStepKind = rows.find(
      (b) => (b.payload as { stepKind?: unknown } | undefined)?.stepKind === runnerKind
    );
    if (byStepKind) return byStepKind;
    return rows.find((b) => b.key === runnerKind) ?? null;
  }, [rows, runnerKind]);

  const [entries, setEntries] = React.useState<Entry[]>([]);
  // Reseed local entries whenever the server block's primitives change (settled round-trips only).
  const sig = block ? `${block.id}:${JSON.stringify((block.payload as { primitives?: unknown }).primitives ?? null)}` : 'none';
  React.useEffect(() => {
    setEntries(readEntries(block));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const persist = React.useCallback(
    (next: Entry[]) => {
      const payload = {
        ...((block?.payload as Record<string, unknown>) ?? {}),
        stepKind: runnerKind,
        primitives: buildPrimitives(next),
      };
      if (block) {
        mut.update.mutate({ ...block, payload, id: block.id });
      } else {
        mut.create.mutate({
          campaign: campaign.id,
          parent: null,
          category: STEP_LAYOUT_CATEGORY,
          key: runnerKind,
          label: `${primitiveLabel(runnerKind) || runnerKind} body`,
          order: 0,
          hidden: false,
          payload,
          metadata: {},
        });
      }
    },
    [block, runnerKind, campaign.id, mut.update, mut.create]
  );

  const commit = React.useCallback(
    (next: Entry[]) => {
      setEntries(next);
      persist(next);
    },
    [persist]
  );

  const addPrimitive = (key: string) => {
    if (!canWrite) return;
    commit([...entries, { id: freshId(), key, config: {} }]);
  };

  const removeEntry = (id: string) => {
    if (!canWrite) return;
    commit(entries.filter((e) => e.id !== id));
  };

  // A local-only config edit (controlled inputs); the value is flushed to the server on blur.
  const patchConfig = (id: string, field: string, value: unknown) =>
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, config: { ...e.config, [field]: value } } : e))
    );

  // Flush the current entries to the server (text fields: on blur; boolean: immediately after change).
  const flush = () => persist(entries);

  const setBoolean = (id: string, field: string, value: boolean) => {
    const next = entries.map((e) =>
      e.id === id ? { ...e, config: { ...e.config, [field]: value } } : e
    );
    commit(next);
  };

  // ReactSortable's controlled body list: reorders and palette drops both arrive here. Palette clones
  // carry a non-instance id (the primitive key), so we mint a fresh entry for them and keep existing
  // entries by their instance id. A no-op echo (same shape) is ignored to avoid a redundant PUT.
  const onBodyList = (newList: Array<{ id: string; key?: string }>) => {
    if (!canWrite) return;
    const next: Entry[] = newList
      .map((item): Entry => {
        if (isInstanceId(item.id)) {
          return entries.find((e) => e.id === item.id) ?? { id: item.id, key: item.key ?? '', config: {} };
        }
        return { id: freshId(), key: item.key ?? String(item.id), config: {} };
      })
      .filter((e) => e.key);
    if (entriesShape(next) === entriesShape(entries) && next.length === entries.length) return;
    commit(next);
  };

  const palette = React.useMemo(
    () => STEP_LAYOUT_PRIMITIVES.map((d) => ({ id: d.key, key: d.key })),
    []
  );

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Step body layout"
        subtitle="Compose this step's body from primitives. Drag one from the palette onto the body (or click ＋), reorder by dragging, and set each primitive's optional overrides. The runner renders them top to bottom."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* --- Palette --- */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Primitives
            </Typography>
            <ReactSortable
              list={palette}
              setList={() => {}}
              group={{ name: 'step-primitives', pull: 'clone', put: false }}
              sort={false}
              disabled={!canWrite}
              animation={150}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
            >
              {STEP_LAYOUT_PRIMITIVES.map((def) => (
                <Box
                  key={def.key}
                  title={def.description}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    border: '1px dashed var(--cb-border, #e5e7eb)',
                    borderRadius: 1,
                    cursor: canWrite ? 'grab' : 'default',
                    userSelect: 'none',
                    fontSize: 13,
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <span>{def.label}</span>
                  {canWrite && (
                    <Tooltip title="Add to body">
                      <IconButton
                        size="small"
                        onClick={() => addPrimitive(def.key)}
                        aria-label={`Add ${def.label}`}
                        sx={{ p: 0.25 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </ReactSortable>
          </Box>

          {/* --- Body --- */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Body ({entries.length})
            </Typography>
            {entries.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No primitives yet — this step renders an empty body. Drag one from the palette above, or
                click its ＋, to start composing.
              </Typography>
            )}
            <ReactSortable
              list={entries}
              setList={onBodyList}
              group={{ name: 'step-primitives', put: true, pull: false }}
              handle=".cb-primitive-drag"
              disabled={!canWrite}
              animation={150}
              style={{
                minHeight: entries.length ? undefined : 56,
                border: entries.length ? undefined : '1px dashed var(--cb-border, #e5e7eb)',
                borderRadius: 8,
              }}
            >
              {entries.map((entry) => {
                const def = primitiveDef(entry.key);
                const fields: PrimitiveField[] = def?.fields ?? [];
                return (
                  <Box
                    key={entry.id}
                    sx={{
                      border: '1px solid var(--cb-border, #e5e7eb)',
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: fields.length ? 1 : 0 }}>
                      <Box
                        className="cb-primitive-drag"
                        sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', display: 'flex' }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {primitiveLabel(entry.key)}
                        </Typography>
                        {!def && (
                          <Typography variant="caption" color="warning.main">
                            Unknown primitive — the runner will skip it. Remove or fix the key.
                          </Typography>
                        )}
                      </Box>
                      {canWrite && (
                        <Tooltip title="Remove from body">
                          <IconButton size="small" onClick={() => removeEntry(entry.id)} aria-label="Remove primitive">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    {fields.length > 0 && (
                      <Stack spacing={1}>
                        {fields.map((field) =>
                          field.type === 'boolean' ? (
                            <FormControlLabel
                              key={field.key}
                              control={
                                <Switch
                                  checked={!!entry.config[field.key]}
                                  disabled={!canWrite}
                                  onChange={(e) => setBoolean(entry.id, field.key, e.target.checked)}
                                />
                              }
                              label={
                                <Box>
                                  <Box component="span" sx={{ fontSize: 14 }}>
                                    {field.label}
                                  </Box>
                                  {field.help && (
                                    <Box
                                      component="span"
                                      sx={{ display: 'block', fontSize: '0.72rem', color: 'text.secondary' }}
                                    >
                                      {field.help}
                                    </Box>
                                  )}
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                          ) : (
                            <TokenTextField
                              key={field.key}
                              label={field.label}
                              campaignId={campaign.id}
                              multiline={field.type === 'multiline'}
                              minRows={field.type === 'multiline' ? 2 : undefined}
                              value={String(entry.config[field.key] ?? '')}
                              disabled={!canWrite}
                              helperText={field.help}
                              onChange={(next) => patchConfig(entry.id, field.key, next)}
                              onBlur={flush}
                            />
                          )
                        )}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </ReactSortable>
          </Box>
        </Stack>
      )}
    </div>
  );
}

export default StepLayoutComposer;
